import { useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction, type ReactNode } from "react";
import { Link, useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import type { BoardReferencePerformanceSnapshot, Offer, Product, TemplateStudioType, TemplateStudioLibraryImage } from "@shared/schema";
import type { ConceptBoardGenerationInputs } from "@shared/concept-board-generation-inputs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PickerSelect } from "@/components/picker-select";
import { BoardTypeIcon } from "@/components/board-type-icon";
import { ConceptBoardsWorkspace, ConceptBoardsWorkspaceHeader } from "@/components/concept-boards-workspace";
import { ConceptBoardReferencePreview } from "@/components/concept-board-reference-preview";
import { useConceptBoardHistoryPageSize } from "@/hooks/use-concept-board-history-page-size";
import { queryClient } from "@/lib/queryClient";
import {
  advanceSelectionContext, boardReferenceIdsForAdmission, formatPerformanceMetricLabel,
  freezeBoardSubmission, MAX_BOARD_REFERENCES, selectedReferenceCount,
  sameSelectionContext, toggleLibraryReference, type ReferenceSelection, type SelectionContextVersion,
} from "@/lib/concept-board-reference-selection";
import { ArrowRight, Box, Building2, CalendarDays, FileText, Images, Layers3, LayoutGrid, Package, Plus, Sparkles, type LucideIcon } from "lucide-react";

type Panel = { position: number; name: string; concept: string; distinction: string; typeFit: string; scene: string; composition: string; promotedTemplateId: string | null };
type Finding = { position: number; issue: string; correction: string };
type Board = {
  jobId: string; status: string; stage: string; imageUrl: string | null; renderModel: string;
  generationInputs?: ConceptBoardGenerationInputs;
  reviewerEnabled: boolean;
  plan: { panels?: Panel[] } | null; panels: Panel[];
  review: { verdict: string; findings: Finding[] } | null;
  promoted: { position: number; templateId: string }[];
  correctionAvailable: boolean; reviewRetryAvailable: boolean;
  attempts: { attemptId: string; stage: string; status: string; errorCategory: string | null; error: string | null }[];
};
type History = { jobId: string; status: string; stage: string; typeId: string; createdAt: string; imageUrl: string | null; renderModel: string };
type LibraryReference = Omit<TemplateStudioLibraryImage, "enabled"> & {
  enabled?: boolean;
  previewUrl: string;
  performanceSnapshot?: BoardReferencePerformanceSnapshot | null;
};

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, { credentials: "include", ...options });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || data.message || `Request failed (${response.status})`);
  return data as T;
}

export function boardPositions() { return Array.from({ length: 8 }, (_, i) => i + 1); }
export function eligiblePositions(board: Board): number[] {
  if (!["ready", "needs_review"].includes(board.status) || !board.imageUrl) return [];
  const promoted = new Set(board.promoted.map((item) => item.position));
  return boardPositions().filter((position) =>
    board.panels.some((p) => p.position === position) && !promoted.has(position) &&
    !board.panels.some((p) => p.position === position && p.promotedTemplateId));
}

const activeStages = new Set(["queued", "planning", "rendering", "reviewing", "repairing"]);
// Temporarily hidden from creation; keep the choice and its brand-only rendering path for re-enablement.
const BRAND_ONLY_CREATION_ENABLED = false;
const creationGroundingChoices = [
  ...(BRAND_ONLY_CREATION_ENABLED ? [{ mode: "brand", label: "Brand only", icon: Building2 } as const] : []),
  { mode: "product", label: "Product", icon: Package },
  { mode: "offer", label: "Offer", icon: Sparkles },
] as const;
const stageLabel: Record<string, string> = {
  queued: "Queued", planning: "Planning eight concepts", rendering: "Rendering the board",
  reviewing: "Reviewing panels", repairing: "Correcting the board",
  ready: "Ready", needs_review: "Needs review", failed: "Failed", cancelled: "Cancelled",
};

export default function ConceptBoardsPage() {
  const { brandId = "" } = useParams<{ brandId: string }>();
  const base = `/api/brands/${encodeURIComponent(brandId)}/template-studio/boards`;
  const [typeId, setTypeId] = useState("");
  const [item, setItem] = useState("");
  const [groundingMode, setGroundingMode] = useState<"brand" | "product" | "offer">("product");
  const [brief, setBrief] = useState("");
  const [referenceSelection, setReferenceSelection] = useState<ReferenceSelection>({
    referenceIds: [], explicit: false,
  });
  const [referenceSelectionError, setReferenceSelectionError] = useState("");
  const createLockRef = useRef(false);
  const currentContext = `${brandId}:${typeId}`;
  const contextVersionRef = useRef<SelectionContextVersion>({ context: currentContext, generation: 0 });
  contextVersionRef.current = advanceSelectionContext(contextVersionRef.current, currentContext);
  const [selectedId, setSelectedId] = useState("");
  const [historyPage, setHistoryPage] = useState(0);
  const historyPageSize = useConceptBoardHistoryPageSize();
  const [checked, setChecked] = useState<number[]>([]);
  const [pending, setPending] = useState<number[]>([]);
  const [panelErrors, setPanelErrors] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const createButtonRef = useRef<HTMLButtonElement>(null);
  // Persist the exact body with its key until a successful admission. A network
  // timeout must not make a retry charge for a second board.
  const [submission, setSubmission] = useState<{ key: string; body: string } | null>(null);
  const { data: types = [], error: typesError, isLoading: typesLoading } = useQuery<TemplateStudioType[]>({
    queryKey: ["/api/template-studio/types", brandId],
    queryFn: () => request(`/api/template-studio/types?brandId=${encodeURIComponent(brandId)}`),
    enabled: !!brandId, retry: false,
  });
  const { data: products = [] } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`], enabled: !!brandId,
  });
  const { data: offers = [] } = useQuery<Offer[]>({
    queryKey: [`/api/brands/${brandId}/offers`], enabled: !!brandId,
  });
  const { data: referenceRows = [], error: referencesError, isLoading: referencesLoading } = useQuery<LibraryReference[]>({
    queryKey: [`/api/brands/${brandId}/template-studio/library-images`, typeId],
    queryFn: () => request(`/api/brands/${encodeURIComponent(brandId)}/template-studio/library-images?typeId=${encodeURIComponent(typeId)}`),
    enabled: !!typeId, retry: false,
  });
  const references = useMemo(() => referenceRows.filter((reference) => reference.enabled !== false), [referenceRows]);
  const { data: history = [], error: historyError, isLoading: historyLoading } = useQuery<History[]>({
    queryKey: [base], queryFn: () => request(base), enabled: !!brandId, retry: false,
    refetchInterval: (query) => query.state.data?.some((job) => activeStages.has(job.status)) ? 5000 : false,
  });
  const historyPageCount = Math.max(1, Math.ceil(history.length / historyPageSize));
  const currentHistoryPage = Math.min(historyPage, historyPageCount - 1);
  const historyStart = currentHistoryPage * historyPageSize;
  const visibleHistory = history.slice(historyStart, historyStart + historyPageSize);
  useEffect(() => {
    setHistoryPage((page) => Math.min(page, historyPageCount - 1));
  }, [historyPageCount]);
  const { data: board, error: boardError, isLoading: boardLoading } = useQuery<Board>({
    queryKey: [base, selectedId],
    queryFn: () => request(`${base}/${encodeURIComponent(selectedId)}`),
    enabled: !!selectedId, retry: false,
    refetchInterval: (query) => query.state.data && activeStages.has(query.state.data.status) ? 4000 : false,
  });
  useEffect(() => {
    if (!selectedId && history.length) setSelectedId(history[0].jobId);
  }, [history, selectedId]);
  useEffect(() => {
    setSelectedId(""); setTypeId(""); setItem(""); setGroundingMode("product"); setBrief("");
    setHistoryPage(0);
    setSubmission(null); setChecked([]); setPanelErrors({}); setError(""); setMessage("");
    setReferenceSelection({ referenceIds: [], explicit: false });
    setReferenceSelectionError("");
  }, [brandId]);
  useEffect(() => { setChecked([]); setPanelErrors({}); }, [selectedId]);
  useEffect(() => {
    setReferenceSelection({ referenceIds: [], explicit: false });
    setReferenceSelectionError("");
  }, [typeId]);
  useEffect(() => {
    if (submission) return;
    const enabledIds = new Set(references.map((reference) => reference.id));
    setReferenceSelection((previous) => {
      if (!previous.explicit) return previous;
      const referenceIds = previous.referenceIds.filter((id) => enabledIds.has(id));
      return referenceIds.length === previous.referenceIds.length
        ? previous
        : { ...previous, referenceIds };
    });
    setReferenceSelectionError("");
  }, [references, submission]);

  const currentReferenceSelection = useMemo(() => {
    if (!referenceSelection.explicit) return referenceSelection;
    const enabledIds = new Set(references.map((reference) => reference.id));
    return {
      ...referenceSelection,
      referenceIds: referenceSelection.referenceIds.filter((id) => enabledIds.has(id)),
    };
  }, [referenceSelection, references]);
  const selectedReferenceTotal = selectedReferenceCount(currentReferenceSelection, references);

  function isCurrentSelectionContext(captured: SelectionContextVersion) {
    return sameSelectionContext(contextVersionRef.current, captured);
  }

  function changeLibraryReference(referenceId: string, checked: boolean) {
    if (checked && !currentReferenceSelection.referenceIds.includes(referenceId) &&
        selectedReferenceTotal >= MAX_BOARD_REFERENCES && currentReferenceSelection.explicit) {
      setReferenceSelectionError("Select no more than eight references.");
      return;
    }
    const next = toggleLibraryReference(currentReferenceSelection, references, referenceId, checked);
    if (selectedReferenceCount(next, references) > MAX_BOARD_REFERENCES) {
      setReferenceSelectionError("Select no more than eight references.");
      return;
    }
    setReferenceSelection(next);
    setReferenceSelectionError("");
  }

  async function act(path: string) {
    if (!selectedId) return;
    setBusy(true); setError(""); setMessage("");
    try {
      await request(`${base}/${encodeURIComponent(selectedId)}/${path}`, { method: "POST" });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [base] }),
        queryClient.invalidateQueries({ queryKey: [base, selectedId] }),
      ]);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  async function create() {
    if (!typeId || (!submission && !validGroundingItem)) return;
    if (createLockRef.current) return;
    createLockRef.current = true;
    const capturedContext = contextVersionRef.current;
    setBusy(true); setError(""); setMessage("");
    try {
      let next = submission;
      if (!next) {
        const ids = boardReferenceIdsForAdmission(currentReferenceSelection, references.length);
        next = freezeBoardSubmission(crypto.randomUUID(), {
          typeId, columns: 4, rows: 2, brief: brief.trim() || null,
          ...(item ? { promotedItem: { type: item.split(":")[0], id: item.slice(item.indexOf(":") + 1) } } : {}),
          ...(ids !== undefined ? { referenceIds: ids } : {}),
        });
        setSubmission(next);
      }
      const admitted = await request<{ jobId: string }>(base, {
        method: "POST", headers: { "Content-Type": "application/json", "Idempotency-Key": next.key },
        body: next.body,
      });
      if (!isCurrentSelectionContext(capturedContext)) return;
      setSubmission(null);
      setSelectedId(admitted.jobId);
      setHistoryPage(0);
      setCreateOpen(false);
      await queryClient.invalidateQueries({ queryKey: [base] });
      setMessage("Board admitted. Progress will continue across refreshes.");
    } catch (e) {
      if (isCurrentSelectionContext(capturedContext)) setError((e as Error).message);
    }
    finally {
      createLockRef.current = false;
      setBusy(false);
    }
  }

  async function promote() {
    if (!board) return;
    const positions = checked.filter((p) => eligiblePositions(board).includes(p)).sort((a, b) => a - b);
    if (!positions.length) return;
    setPending(positions); setPanelErrors({}); setError("");
    try {
      await request(`${base}/${encodeURIComponent(board.jobId)}/promote`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ positions }),
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      // The server commits each crop separately. Even when the request fails,
      // reconcile committed positions rather than assuming the entire batch
      // failed and losing evidence of already promoted panels.
      try {
        const fresh = await request<Board>(`${base}/${encodeURIComponent(board.jobId)}`);
        queryClient.setQueryData([base, board.jobId], fresh);
        const done = new Set(fresh.promoted.map((p) => p.position));
        setChecked((prev) => prev.filter((p) => !done.has(p)));
        setPanelErrors(Object.fromEntries(positions.filter((p) => !done.has(p)).map((p) => [p, "Not promoted. Check the board status and retry."])));
      } catch (e) { setError(`Could not confirm promotion status: ${(e as Error).message}`); }
      await queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/ad-units`] });
      setPending([]);
    }
  }

  const eligible = board ? eligiblePositions(board) : [];
  const activeProducts = products.filter((p) => p.status === "active" && !p.archivedAt);
  const activeOffers = offers.filter((o) => o.status === "active" && !o.archivedAt);
  const validGroundingItem = groundingMode === "brand"
    ? BRAND_ONLY_CREATION_ENABLED
    : groundingMode === "product"
      ? activeProducts.some((p) => item === `product:${p.id}`)
      : activeOffers.some((o) => item === `offer:${o.id}`);
  const enabledTypes = types.filter((t) => t.enabled);
  function chooseGroundingMode(next: "brand" | "product" | "offer") {
    if (next === groundingMode) return;
    setGroundingMode(next);
    setItem("");
  }
  return <ConceptBoardsWorkspace>
    <ConceptBoardsWorkspaceHeader title="Concept Boards" description="Compare eight directions at a glance. Keep the promising panels; promote only the ones worth building.">
      <Button ref={createButtonRef} size="lg" onClick={() => setCreateOpen(true)} className="h-11 shrink-0 gap-2 rounded-xl bg-violet-700 px-5 text-white hover:bg-violet-800">
        <Plus className="h-4 w-4" /> Create new board
      </Button>
    </ConceptBoardsWorkspaceHeader>
    {error && !createOpen && <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-rose-800">{error}</p>}
    {message && <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-800">{message}</p>}
    <Dialog open={createOpen} onOpenChange={setCreateOpen}>
      <DialogContent onCloseAutoFocus={(event) => { event.preventDefault(); createButtonRef.current?.focus(); }}
        className="flex max-h-[min(92dvh,900px)] max-w-2xl flex-col gap-0 overflow-hidden rounded-2xl border-slate-200 bg-[#fffefd] p-0 shadow-[0_28px_90px_-30px_rgba(39,28,68,0.4)]">
        <DialogHeader className="shrink-0 border-b border-slate-200/80 px-6 py-5 pr-12 text-left sm:px-8">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-violet-700">Create new board</p>
          <DialogTitle className="font-display text-2xl tracking-tight text-slate-900">Create a new concept board</DialogTitle>
          <DialogDescription className="leading-5">Choose a board type, anchor it to your product or offer, then add optional context and saved references.</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-8">
      <section aria-labelledby="board-type-heading" className="flex gap-3">
        <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-semibold text-violet-800">1</span>
        <div className="min-w-0 flex-1 space-y-3">
          <div><h2 id="board-type-heading" className="font-semibold">Choose a board type</h2>
            <p className="text-sm text-muted-foreground">Each type has its own format and visual requirements.</p></div>
          {typesLoading ? <p role="status" className="text-sm text-muted-foreground">Loading board types…</p> : typesError ?
            <p role="alert" className="text-sm text-destructive">Could not load board types.</p> : !enabledTypes.length ?
            <p className="text-sm text-muted-foreground">No board types are available for this brand.</p> :
            <PickerSelect icon={<Layers3 className="h-4 w-4" />} label="Board type" placeholder="Choose a type"
              value={typeId} onChange={setTypeId} options={enabledTypes.map((t) => ({
                value: t.id, label: t.name, description: t.definition, icon: <BoardTypeIcon name={t.name} className="h-4 w-4" />,
              }))}
               testId="select-board-type" searchable wrapDescription disabled={!!submission || busy} />}
        </div>
      </section>
      <section aria-labelledby="board-details-heading" className="mt-7 flex gap-3">
        <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-semibold text-violet-800">2</span>
        <div className="min-w-0 flex-1 space-y-4">
           <div><h2 id="board-details-heading" className="font-semibold">Choose grounding</h2>
             <p className="text-sm text-muted-foreground">Select an active product or offer. A brief and visual references are optional.</p></div>
          <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs font-medium">Grounding</span>
            <div role="group" aria-label="Grounding mode" className="flex flex-wrap items-center gap-1 rounded-xl border border-border/70 bg-background/60 p-0.5 shadow-sm">
              {creationGroundingChoices.map(({ mode, label, icon: Icon }) => <Button key={mode} type="button" size="sm"
                variant={groundingMode === mode ? "default" : "ghost"}
                className="h-7 gap-1 rounded-full px-3 text-[11px]"
                aria-pressed={groundingMode === mode}
                disabled={!!submission}
                onClick={() => chooseGroundingMode(mode)}
                data-testid={`toggle-grounding-${mode}`}>
                <Icon className="h-3 w-3" />{label}
              </Button>)}
            </div>
          </div>
           {groundingMode === "brand" && BRAND_ONLY_CREATION_ENABLED ? <div className="flex min-h-[72px] items-center gap-3 rounded-xl border border-border/60 bg-background/60 px-3 py-2.5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted/70 text-muted-foreground ring-1 ring-border/60">
              <Building2 className="h-4 w-4" />
            </div>
            <div><span className="block text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/70">Grounding</span>
              <span className="text-sm text-muted-foreground">Ungrounded — brand only</span></div>
          </div> : groundingMode === "product" ? <PickerSelect
            icon={<Package className="h-4 w-4" />}
            label="Product"
            placeholder={activeProducts.length ? "Choose product" : "No active products"}
            value={item.startsWith("product:") ? item.slice("product:".length) : ""}
            onChange={(id) => setItem(`product:${id}`)}
            options={[...activeProducts]
              .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
              .map((p) => {
              const media = (p as { mediaJson?: string[] | null }).mediaJson;
              return { value: p.id, label: p.name, imageUrl: Array.isArray(media) ? media[0] : null };
            })}
            testId="select-board-product"
            searchable
            disabled={!!submission || activeProducts.length === 0}
          /> : <PickerSelect
            icon={<Sparkles className="h-4 w-4" />}
            label="Offer"
            placeholder={activeOffers.length ? "Choose offer" : "No active offers"}
            value={item.startsWith("offer:") ? item.slice("offer:".length) : ""}
            onChange={(id) => setItem(`offer:${id}`)}
            options={activeOffers.map((o) => ({ value: o.id, label: o.name,
              description: (o as Offer & { shortDescription?: string | null; summary?: string | null }).shortDescription
                || (o as Offer & { summary?: string | null }).summary || null }))}
            testId="select-board-offer"
            searchable
            disabled={!!submission || activeOffers.length === 0}
          />}
          </div>
      </div>
      </section>
      <section aria-labelledby="board-brief-heading" className="mt-6 flex gap-3">
        <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-semibold text-violet-800">3</span>
        <div className="min-w-0 flex-1">
        <div className="mb-2 flex items-center gap-3">
          <div><Label id="board-brief-heading" htmlFor="board-brief" className="font-semibold">Add a brief (optional)</Label><p className="text-xs text-muted-foreground">A short note to guide the creative direction.</p></div>
        </div>
        <textarea id="board-brief" className="w-full rounded-xl border border-slate-200 bg-white p-3 text-sm shadow-sm outline-none transition focus-visible:ring-2 focus-visible:ring-violet-500" maxLength={10000} rows={3}
          disabled={!!submission} value={brief} onChange={(e) => setBrief(e.target.value)} />
        </div>
      </section>
      {typeId && <section aria-label="Board references" className="mt-6 space-y-3 border-t border-slate-200 pt-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="font-medium">References</h3>
            <p className="text-xs text-muted-foreground">Choose references from your Board references library.</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">{selectedReferenceTotal} of 8 selected</span>
            <Link href={`/brands/${brandId}/template-studio/library`} className="text-xs underline">
              Manage Board references
            </Link>
          </div>
        </div>
        {referenceSelectionError && <p role="alert" className="text-sm text-destructive">{referenceSelectionError}</p>}
        {referencesError ? <p role="alert" className="text-sm text-destructive">
          Could not load references for this type. <Button type="button" variant="outline" size="sm"
            onClick={() => queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/template-studio/library-images`, typeId] })}>Retry references</Button>
        </p> : referencesLoading ? <p role="status" className="text-sm text-muted-foreground">Loading references...</p>
          : references.length === 0 ? <p className="text-sm text-muted-foreground">
            No enabled references are available for this type yet. Open Board references to import, classify, or enable images.
          </p> : <>
            <p className="text-sm text-muted-foreground">
              {references.length} classified references available. {references.length > MAX_BOARD_REFERENCES
                ? "Choose up to eight below."
                : "They will be included automatically unless you make a selection."}
            </p>
            <div className="grid gap-2 sm:grid-cols-2" aria-label="Library references">
              {references.map((reference, index) => <div key={reference.id}
                className="flex min-w-0 items-center gap-3 rounded-lg border p-2">
                <Checkbox id={`library-reference-${reference.id}`} aria-label={`Select reference ${reference.id}`}
                  checked={referenceSelection.explicit
                    ? referenceSelection.referenceIds.includes(reference.id)
                    : references.length <= MAX_BOARD_REFERENCES}
                  disabled={!!submission || busy ||
                    (referenceSelection.explicit && !referenceSelection.referenceIds.includes(reference.id) &&
                      selectedReferenceTotal >= MAX_BOARD_REFERENCES)}
                  onCheckedChange={(value) => changeLibraryReference(reference.id, value === true)} />
                <ConceptBoardReferencePreview src={reference.previewUrl} label={`Reference ${index + 1}`} />
                <Label htmlFor={`library-reference-${reference.id}`} className="min-w-0 flex-1 space-y-1 cursor-pointer">
                  <span className="block truncate text-xs text-muted-foreground">
                    {reference.source === "performance" ? "Imported reference" : "Uploaded reference"}
                  </span>
                  {reference.performanceSnapshot && <span className="block text-xs text-muted-foreground">
                    {formatPerformanceMetricLabel(reference.performanceSnapshot)}
                  </span>}
                </Label>
              </div>)}
            </div>
          </>}
      </section>}
        </div>
      <DialogFooter className="shrink-0 flex-col gap-2 border-t border-slate-200 bg-slate-50/80 px-5 py-4 sm:flex-row sm:flex-wrap sm:px-8">
        {error && <p role="alert" className="min-w-0 basis-full break-words text-left text-sm text-rose-700">{error}</p>}
        {submission && <Button variant="ghost" disabled={busy} onClick={() => setSubmission(null)}>Start a different board</Button>}
        <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Close</Button>
        <Button size="lg" className="gap-2 rounded-xl bg-violet-700 text-white hover:bg-violet-800" disabled={!typeId || busy || (!submission && (referencesLoading || !!referencesError || !validGroundingItem))} onClick={create}>{busy ? "Submitting…" : submission ? "Retry same submission" : "Create board"}<ArrowRight className="h-4 w-4" /></Button>
      </DialogFooter>
      </DialogContent>
    </Dialog>
    <section aria-labelledby="recent-boards-heading" className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div><p className="text-[10px] font-bold uppercase tracking-[0.17em] text-violet-700">Your workspace</p><h2 id="recent-boards-heading" className="font-display text-xl font-semibold text-slate-900">Recent boards</h2></div>
        <span className="text-xs text-slate-500">{history.length} {history.length === 1 ? "board" : "boards"}</span>
      </div>
      {historyLoading && <p role="status" className="text-sm text-muted-foreground">Loading recent boards…</p>}
      {historyError && <p role="alert" className="text-sm text-destructive">Could not load board history. <Button variant="outline" size="sm" onClick={() => queryClient.invalidateQueries({ queryKey: [base] })}>Retry</Button></p>}
      {!historyLoading && !historyError && !history.length && <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center">
        <p className="font-medium text-slate-800">No boards in this workspace yet.</p><p className="mt-1 text-sm text-slate-500">Create one to compare eight concepts side by side.</p>
      </div>}
      <div id="recent-boards-grid" className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {visibleHistory.map((entry) => {
          const name = types.find((t) => t.id === entry.typeId)?.name ?? "Board type unavailable";
          return <button key={entry.jobId} type="button" onClick={() => setSelectedId(entry.jobId)}
            aria-pressed={selectedId === entry.jobId} data-testid={`recent-board-${entry.jobId}`}
             className={`group flex w-full min-w-0 items-center gap-3 rounded-2xl border p-3 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 ${selectedId === entry.jobId ? "border-violet-500 bg-violet-50/70 shadow-[0_4px_18px_-12px_rgba(91,55,172,0.5)]" : "border-slate-200 bg-white hover:border-violet-300 hover:bg-violet-50/30"}`}>
             <RecentBoardThumbnail imageUrl={entry.imageUrl} name={name} />
             <span className="min-w-0 flex-1"><span className="block truncate font-semibold text-slate-900">{name}</span>
               <span className="mt-1 flex items-center gap-1.5 text-xs text-slate-600"><span className={`h-1.5 w-1.5 rounded-full ${entry.status === "ready" ? "bg-emerald-500" : entry.status === "failed" ? "bg-rose-500" : entry.status === "needs_review" ? "bg-amber-500" : "bg-violet-500"}`} />{stageLabel[entry.status] ?? entry.status}</span>
               <span className="mt-1 block text-[11px] text-slate-500">{new Date(entry.createdAt).toLocaleString()}</span></span>
             <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
          </button>;
        })}
      </div>
      {historyPageCount > 1 && <nav aria-label="Recent boards pagination" className="flex flex-wrap items-center justify-between gap-3">
        <p role="status" className="text-xs text-slate-500">
          Showing {historyStart + 1}–{Math.min(historyStart + historyPageSize, history.length)} of {history.length} boards
        </p>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" aria-controls="recent-boards-grid"
            disabled={currentHistoryPage === 0} onClick={() => setHistoryPage(currentHistoryPage - 1)}>Previous</Button>
          <span className="text-xs text-slate-600">Page {currentHistoryPage + 1} of {historyPageCount}</span>
          <Button type="button" variant="outline" size="sm" aria-controls="recent-boards-grid"
            disabled={currentHistoryPage === historyPageCount - 1} onClick={() => setHistoryPage(currentHistoryPage + 1)}>Next</Button>
        </div>
      </nav>}
    </section>
    {selectedId && <section aria-label="Selected board" className="space-y-5">
      <h2 className="sr-only">Selected board</h2>
      {boardLoading && <p role="status">Loading board details…</p>}
      {boardError && <p role="alert">Could not load this board. <Button variant="outline" onClick={() => queryClient.invalidateQueries({ queryKey: [base, selectedId] })}>Retry</Button></p>}
      {board && <BoardDetail key={board.jobId} board={board} typeName={types.find((t) => t.id === history.find((entry) => entry.jobId === selectedId)?.typeId)?.name ?? "Board type unavailable"}
        createdAt={history.find((entry) => entry.jobId === selectedId)?.createdAt ?? null}
        eligible={eligible} checked={checked} pending={pending} panelErrors={panelErrors}
        busy={busy} setChecked={setChecked} act={act} promote={promote} />}
    </section>}
  </ConceptBoardsWorkspace>;
}

function RecentBoardThumbnail({ imageUrl, name }: { imageUrl: string | null; name: string }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  return <span className="flex h-[68px] w-[88px] shrink-0 items-center justify-center overflow-hidden rounded-xl bg-slate-100 text-slate-500 ring-1 ring-slate-200">
    {imageUrl && failedUrl !== imageUrl
      ? <img src={imageUrl} alt="" className="h-full w-full object-cover" onError={() => setFailedUrl(imageUrl)} />
      : <BoardTypeIcon name={name} />}
  </span>;
}

function BoardDetail({ board, typeName, createdAt, eligible, checked, pending, panelErrors, busy, setChecked, act, promote }: {
  typeName: string; createdAt: string | null;
  board: Board; eligible: number[]; checked: number[]; pending: number[]; panelErrors: Record<number, string>;
  busy: boolean; setChecked: Dispatch<SetStateAction<number[]>>;
  act: (path: string) => void; promote: () => void;
}) {
  const promoted = new Set(board.promoted.map((p) => p.position));
  const reviewFailed = board.reviewerEnabled && board.attempts.filter((a) => a.stage.startsWith("review")).at(-1)?.status === "failed";
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => { setImageFailed(false); }, [board.imageUrl]);
  const selectedEligible = checked.filter((position) => eligible.includes(position)).length;
  return <>
    <div data-testid="selected-board-summary" className="min-w-0 rounded-2xl border border-slate-200 bg-[#fffefd] p-4 shadow-sm sm:px-5">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.17em] text-violet-700">Selected board</p>
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-600">
          <h2 className="min-w-0 break-words font-display text-xl font-semibold text-slate-900">{board.generationInputs?.boardTypeName ?? typeName}</h2>
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 ${board.status === "ready" ? "bg-emerald-50 text-emerald-700" : board.status === "failed" ? "bg-rose-50 text-rose-700" : board.status === "needs_review" ? "bg-amber-50 text-amber-700" : "bg-violet-50 text-violet-700"}`}><span className={`h-2 w-2 rounded-full ${board.status === "ready" ? "bg-emerald-500" : board.status === "failed" ? "bg-rose-500" : board.status === "needs_review" ? "bg-amber-500" : "bg-violet-500"}`} />{stageLabel[board.status] ?? board.status}</span>
          {createdAt && <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />Created {new Date(createdAt).toLocaleString()}</span>}
          <span className="inline-flex items-center gap-1.5"><LayoutGrid className="h-3.5 w-3.5" aria-hidden="true" />8 panels · 4 × 2</span>
        </div>
      </div>
      <p className="shrink-0 text-xs text-slate-500">{stageLabel[board.stage] ?? board.stage}</p>
      </div>
      <BoardGenerationInputs inputs={board.generationInputs} />
    </div>
    {(reviewFailed || board.status === "failed" || board.review) && <div className="space-y-2">
      {board.review && <p className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">Review result: <span className="font-medium">{board.review.verdict.replaceAll("_", " ")}</span></p>}
      {reviewFailed && <p role="alert" className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        Automated review could not finish. Check each panel yourself before promoting it.
      </p>}
      {board.status === "failed" && <p role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
        Board generation failed. You can retry the stage if available.
      </p>}
    </div>}
    <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(290px,350px)]">
      <section aria-label="Saved eight-panel contact sheet" className="min-w-0 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-display text-lg font-semibold text-slate-900">Contact sheet</h3>
          <span className="text-xs text-slate-500">4 columns · 2 rows</span>
        </div>
        <div className="relative w-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-sm" style={{ aspectRatio: "8 / 5" }}>
          {board.imageUrl && !imageFailed
            ? <img src={board.imageUrl} alt="Saved eight-panel concept board, arranged in four columns and two rows" className="absolute inset-0 h-full w-full object-contain" onError={() => setImageFailed(true)} />
            : <div role="group" aria-label="Contact sheet image unavailable" className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-100 text-center text-slate-500">
              <Layers3 className="h-8 w-8" /><span className="text-sm font-medium">Contact sheet image unavailable</span>
              {board.imageUrl && <Button type="button" size="sm" variant="outline" className="pointer-events-auto relative z-10" onClick={() => setImageFailed(false)}>Retry image</Button>}
            </div>}
          <div className="absolute inset-0 grid grid-cols-4 grid-rows-2">
            {boardPositions().map((position) => <div key={position} className="border border-white/75 p-1 sm:p-2">
              <span className="inline-flex min-h-6 min-w-6 items-center justify-center rounded-md bg-slate-950/75 px-1.5 text-[11px] font-semibold text-white">{position}{promoted.has(position) ? " ·" : ""}</span>
            </div>)}
          </div>
        </div>
      </section>
      {board.plan && <section aria-labelledby="panel-review-heading" className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div><h3 id="panel-review-heading" className="font-display text-lg font-semibold text-slate-900">Panel review</h3><p className="text-xs text-slate-500">Select panels to promote; selection is not approval.</p></div>
            <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">{selectedEligible} / {eligible.length} eligible</span>
          </div>
        </div>
        <div className="divide-y divide-slate-100">
        {boardPositions().map((position) => {
          const panel = board.panels.find((p) => p.position === position) ?? board.plan?.panels?.find((p) => p.position === position);
          const finding = board.review?.findings?.find((f) => f.position === position);
          const canSelect = eligible.includes(position);
          return <div key={position} className={`space-y-2 px-4 py-3 ${canSelect && checked.includes(position) ? "bg-violet-50/60" : ""}`}>
            <div className="flex min-w-0 items-center gap-2.5">
              <Checkbox id={`panel-${position}`} checked={checked.includes(position)}
                aria-label={`${position}. ${panel?.name ?? "Panel"}`}
                disabled={!canSelect || pending.includes(position)}
                onCheckedChange={(value) => setChecked((prev) => value === true ? (prev.includes(position) ? prev : [...prev, position]) : prev.filter((p) => p !== position))} />
              <Label htmlFor={`panel-${position}`} className="min-w-0 flex-1 cursor-pointer truncate text-sm font-semibold text-slate-800">{position}. {panel?.name ?? "Panel"}</Label>
              {pending.includes(position) && <span role="status" className="shrink-0 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-800">Promoting…</span>}
              {promoted.has(position) && <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">Promoted</span>}
              {!canSelect && !promoted.has(position) && !panel?.promotedTemplateId && <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">{board.status === "ready" || board.status === "needs_review" ? "Unavailable" : "Pending board"}</span>}
            </div>
            {panel?.promotedTemplateId && !promoted.has(position) && <p className="pl-8 text-xs text-slate-600">Previously promoted; template is no longer active or approved.</p>}
            {panelErrors[position] && <p role="alert" className="ml-8 rounded-lg bg-rose-50 px-2.5 py-2 text-xs text-rose-800">{panelErrors[position]}</p>}
            {finding && <div className="ml-8 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs leading-5 text-amber-900">
                <p><strong>Review finding:</strong> {finding.issue}</p><p><strong>Suggested correction:</strong> {finding.correction}</p>
            </div>}
          </div>;
        })}
        </div>
        <div className="space-y-2 border-t border-slate-200 bg-slate-50/70 p-4">
          {board.status !== "ready" && board.status !== "needs_review" && <p className="text-xs leading-5 text-slate-600">Panels can be promoted only after the board is ready or marked for review.</p>}
          <Button className="w-full gap-2 rounded-xl bg-violet-700 text-white hover:bg-violet-800" onClick={promote} disabled={!selectedEligible || pending.length > 0}>
            Promote {selectedEligible} selected panels <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </section>}
    </div>
    {(activeStages.has(board.status) || board.status === "failed" || (board.reviewerEnabled && (board.reviewRetryAvailable || board.correctionAvailable))) && <Card className="rounded-2xl border-slate-200 shadow-sm"><CardHeader className="pb-3"><CardTitle className="font-display text-base">Board controls</CardTitle></CardHeader><CardContent className="space-y-3">
      {board.status === "failed" && !["planning", "rendering"].includes(board.stage) && <p className="text-sm text-slate-600">No generation-stage retry is available for this failure.</p>}
      <div className="flex flex-wrap gap-2">
        {(activeStages.has(board.status) || board.status === "failed") && <Button variant="outline" disabled={busy} onClick={() => act("cancel")}>Cancel board</Button>}
        {board.status === "failed" && ["planning", "rendering"].includes(board.stage) &&
          <Button variant="outline" disabled={busy} onClick={() => act("retry")}>Retry stage</Button>}
        {board.reviewerEnabled && board.reviewRetryAvailable &&
          <Button variant="outline" disabled={busy} onClick={() => act("retry-review")}>Retry review</Button>}
        {board.reviewerEnabled && board.correctionAvailable && <Button variant="outline" disabled={busy} onClick={() => act("correct")}>Run one correction</Button>}
      </div>
    </CardContent></Card>}
  </>;
}

function BoardGenerationInputs({ inputs }: { inputs?: ConceptBoardGenerationInputs }) {
  const [showAllReferences, setShowAllReferences] = useState(false);
  const references = inputs?.references ?? [];
  const grounding = inputs?.grounding;
  const referenceCount = inputs?.selectedReferenceCount;
  const groundingLabel = !grounding ? "Unavailable"
    : grounding.kind === "brand" ? "Brand only"
    : `${grounding.kind === "product" ? "Product" : "Offer"} — ${grounding.name ?? "Name unavailable"}`;
  const referenceLabel = referenceCount == null ? "Unavailable"
    : referenceCount === 0 ? "No saved references"
    : inputs?.referenceSelection === "all_eligible" ? `All eligible (${referenceCount})`
    : inputs?.referenceSelection === "explicit" ? `${referenceCount} selected` : `${referenceCount} saved references`;
  return <section aria-labelledby="generation-inputs-heading" className="mt-3 min-w-0 space-y-3 border-t border-slate-200 pt-3">
    <h3 id="generation-inputs-heading" className="sr-only">Generation inputs</h3>
    <div className="grid gap-2.5 md:grid-cols-3">
      <GenerationInputTile label="Grounding" icon={Box}><p className="break-words font-medium text-slate-800">{groundingLabel}</p></GenerationInputTile>
      <GenerationInputTile label="Saved references" icon={Images}><p className="font-medium text-slate-800">{referenceLabel}</p></GenerationInputTile>
      <GenerationInputTile label="Brief" icon={FileText}><BoardBriefSummary brief={inputs?.brief} unavailable={!inputs} /></GenerationInputTile>
    </div>
    {references.length > 0 && <div>
      <h4 className="mb-2 text-xs font-medium text-slate-600">References ({references.length})</h4>
      <div id="original-reference-images" aria-label="Original reference images" className="flex flex-wrap items-start gap-3">
        {(showAllReferences ? references : references.slice(0, 3)).map((reference, index) => <figure key={reference.id} className="w-14">
          <ConceptBoardReferencePreview src={reference.previewUrl} label={`Reference ${index + 1}`} />
          <figcaption className="sr-only">Reference {index + 1}</figcaption>
        </figure>)}
        {references.length > 3 && <button type="button" aria-expanded={showAllReferences} aria-controls="original-reference-images"
          aria-label={showAllReferences ? "Show fewer references" : `Show ${references.length - 3} more references`}
          onClick={() => setShowAllReferences(!showAllReferences)}
          className="flex h-14 w-14 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-[10px] text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600">
          {!showAllReferences && <Plus className="h-4 w-4" aria-hidden="true" />}
          {showAllReferences ? "Show fewer" : `${references.length - 3} more`}
        </button>}
      </div>
    </div>}
  </section>;
}

function GenerationInputTile({ label, icon: Icon, children }: { label: string; icon: LucideIcon; children: ReactNode }) {
  return <div className="flex min-w-0 items-start gap-3 rounded-xl border border-slate-200/70 bg-slate-50/60 px-3 py-2.5 text-sm">
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500"><Icon className="h-4 w-4" aria-hidden="true" /></span>
    <div className="min-w-0 flex-1"><h4 className="mb-0.5 text-[11px] text-slate-500">{label}</h4>{children}</div>
  </div>;
}

function BoardBriefSummary({ brief, unavailable }: { brief?: string | null; unavailable: boolean }) {
  const previewRef = useRef<HTMLParagraphElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  useEffect(() => {
    const preview = previewRef.current;
    if (!preview) return;
    const measure = () => setOverflowing(preview.scrollHeight > preview.clientHeight + 1);
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(preview);
    window.addEventListener("resize", measure);
    return () => { observer?.disconnect(); window.removeEventListener("resize", measure); };
  }, [brief]);
  if (!brief) return <p className="text-slate-500">{unavailable ? "Original generation inputs unavailable." : "No brief provided."}</p>;
  const expandable = overflowing || brief.length > 120 || brief.includes("\n");
  return <>
    <p ref={previewRef} aria-label="Original brief" className="line-clamp-2 whitespace-pre-wrap break-words font-medium text-slate-800">{brief}</p>
    {expandable && <Dialog>
      <DialogTrigger asChild><button type="button" className="mt-1 text-xs font-medium text-violet-700 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600">View full brief</button></DialogTrigger>
      <DialogContent className="max-h-[85dvh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle>Original brief</DialogTitle><DialogDescription>Saved when this board was generated.</DialogDescription></DialogHeader>
        <p className="whitespace-pre-wrap break-words text-sm text-slate-700">{brief}</p>
      </DialogContent>
    </Dialog>}
  </>;
}